import React from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Icon,
  Input,
  PasswordInput,
  Text,
} from "@salestrak/ui";
import { useLocalSearchParams, useRouter } from "#app/lib/hooks/router-compat";

import { useAuth } from "#app/lib/auth-context";
import { PASSWORD_MIN_LENGTH, newPasswordProblem } from "#app/lib/auth-service";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useAcceptInvitation } from "#app/lib/hooks/use-accept-invitation";
import { AnalyticsContext, WorkspaceEvent, trackEvent } from "#app/lib/analytics";
import { useInvitationByToken } from "#app/lib/hooks/use-invitation-by-token";
import { useSplashAutoFocus } from "#app/lib/splash-context";
import { GoogleSignInButton } from "./google-sign-in-button";

export function AcceptInviteForm({
  signedInEmail = null,
}: {
  /**
   * The session's email when someone signed in opens an invite sent to a
   * different address. Accepting signs in as the invitee and replaces the
   * current session, so the form says so before asking for a password.
   */
  signedInEmail?: string | null;
} = {}) {
  const { token } = useLocalSearchParams<{ token?: string }>();
  const { acceptInvite, acceptInviteWithGoogle, signOut } = useAuth();
  const router = useRouter();
  const [name, setName] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  // Field errors sit under their own field; request-level failures read in
  // the form-level Alert above the CTA.
  const [nameError, setNameError] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [confirmError, setConfirmError] = React.useState("");
  const [formError, setFormError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [googlePending, setGooglePending] = React.useState(false);

  const { loading, details, invalid, invalidReason } =
    useInvitationByToken(token);
  const { acceptInvitation } = useAcceptInvitation();
  const { refreshWorkspaces, switchWorkspace } = useWorkspace();

  React.useEffect(() => {
    if (details?.name) setName(details.name);
  }, [details?.name]);

  const alreadyCreated = details?.alreadyCreated ?? false;
  const busy = submitting || googlePending;
  const nameRef = useSplashAutoFocus();
  const passwordRef = useSplashAutoFocus(alreadyCreated);

  const handleAccept = async () => {
    if (busy) return;
    setFormError("");
    if (!token) {
      setFormError("Invitation link is missing a token.");
      return;
    }
    if (!details) {
      setFormError("This invitation is no longer valid.");
      return;
    }
    if (!password.trim()) {
      setPasswordError("Password is required.");
      return;
    }
    if (!alreadyCreated) {
      if (!name.trim()) {
        setNameError("Full name is required.");
        return;
      }
      const problem = newPasswordProblem(password);
      if (problem) {
        setPasswordError(problem);
        return;
      }
      if (password !== confirmPassword) {
        setConfirmError("Passwords do not match.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const { workspaceId } = await acceptInvite(
        token,
        password,
        alreadyCreated ? undefined : name.trim(),
      );
      await finishAcceptance(
        workspaceId,
        alreadyCreated ? details.name : name.trim(),
        "password",
      );
    } catch (err: any) {
      setFormError(
        err.message ?? "Unable to accept invitation. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Shared by both accept paths: run the core-side membership flip, handle
  // the cancelled-between-steps race, then land in the app. (If an admin
  // cancelled the invite between the auth-side token consume — which issues
  // JWTs — and this call, the workspace User entity is gone and the mutation
  // returns null. Show it instead of redirecting into an empty
  // workspace-select view holding tokens we can't use.)
  const finishAcceptance = async (
    workspaceId: string,
    displayName: string,
    method: "password" | "google",
  ) => {
    const accepted = await acceptInvitation(workspaceId, displayName);
    if (accepted) {
      trackEvent({
        context: AnalyticsContext.Workspace,
        event: WorkspaceEvent.AcceptInvite,
        data: { via: "invite_form", method, newAccount: !alreadyCreated },
      });
    }
    if (!accepted) {
      await signOut();
      setFormError(
        "This invitation has been cancelled. Contact your workspace admin to request a new invite.",
      );
      return;
    }
    // The sign-in above loaded the account's workspaces while this one was
    // still `invited`. Without a fresh list, the app's invite gate asks the
    // new member to join the workspace they have just joined. A new account
    // has no other workspace, so it opens the one it joined, from the fresh
    // row (the list in this render is still the old one).
    const workspaces = await refreshWorkspaces();
    const joined = workspaces?.find((w) => w.workspaceId === workspaceId);
    if (!alreadyCreated && joined) await switchWorkspace(joined);
    router.replace(alreadyCreated ? "/(app)/workspace-select" : "/");
  };

  const handleGoogleCode = async (code: string) => {
    setFormError("");
    // The button only renders once details (and therefore token) resolved —
    // this narrows types rather than guarding a reachable state.
    if (!token || !details) return;
    try {
      const { workspaceId, name: resolvedName } = await acceptInviteWithGoogle(
        token,
        code,
      );
      await finishAcceptance(
        workspaceId,
        resolvedName ?? details.name ?? name.trim(),
        "google",
      );
    } catch (err: any) {
      setFormError(
        err.message ?? "Unable to accept invitation. Please try again.",
      );
    }
  };

  if (invalid) {
    const expired = invalidReason === "expired";
    return (
      <Box direction="column" gap="lg">
        <Alert variant="error">
          <AlertTitle>
            {expired ? "Invitation expired" : "Invitation no longer valid"}
          </AlertTitle>
          <AlertDescription>
            {expired
              ? "This invitation link has expired. Contact your workspace admin to request a new invite."
              : "This invitation has been cancelled or is no longer active. Contact your workspace admin to request a new invite."}
          </AlertDescription>
        </Alert>
        <Box direction="column" paddingTop="lg">
          <Button size="xl" onClick={() => router.replace("/(auth)/login")}>
            Go to login
          </Button>
        </Box>
      </Box>
    );
  }

  const contextLine = details
    ? `${details.inviterName} invited you to join ${details.workspaceName}.`
    : null;

  const displayEmail = details?.email ?? "";

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleAccept();
  };

  return (
    <Box as="form" noValidate direction="column" gap="lg" onSubmit={handleSubmit}>
      {/* One alert, not two: the account mismatch is the fact that matters
          when it applies, so it takes the slot the welcome line otherwise has. */}
      {signedInEmail && details ? (
        <Alert variant="warning">
          <AlertTitle>Signed in as {signedInEmail}</AlertTitle>
          <AlertDescription>
            {`${contextLine} This invitation was sent to ${details.email}; accepting it signs you in as that account instead.`}
          </AlertDescription>
        </Alert>
      ) : contextLine ? (
        <Alert variant="info">
          <AlertTitle>You're almost in</AlertTitle>
          <AlertDescription>{contextLine}</AlertDescription>
        </Alert>
      ) : null}

      {details ? (
        <GoogleSignInButton
          onError={setFormError}
          onCode={handleGoogleCode}
          disabled={submitting}
          onPendingChange={setGooglePending}
        />
      ) : null}

      <Box gap="base">
        <Text
          as="label"
          htmlFor="invite-email"
          size="sm"
          weight="medium"
          variant="muted"
        >
          Email
        </Text>
        <Input
          id="invite-email"
          size="xl"
          type="email"
          value={displayEmail}
          disabled
          readOnly
        />
      </Box>

      {alreadyCreated ? null : (
        <Box gap="base">
          <Text
            as="label"
            htmlFor="invite-name"
            size="sm"
            weight="medium"
            variant="muted"
          >
            Full Name
          </Text>
          <Box gap="sm">
            <Input
              id="invite-name"
              size="xl"
              placeholder="John Smith"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError("");
              }}
              aria-invalid={!!nameError}
              ref={nameRef}
              autoComplete="name"
              disabled={busy || !details}
            />
            {nameError ? (
              <Text size="xs" weight="medium" variant="destructive">
                {nameError}
              </Text>
            ) : null}
          </Box>
        </Box>
      )}

      <Box gap="base">
        <Text
          as="label"
          htmlFor="invite-password"
          size="sm"
          weight="medium"
          variant="muted"
        >
          Password
        </Text>
        <Box gap="sm">
          <PasswordInput
            id="invite-password"
            size="xl"
            placeholder={
              alreadyCreated
                ? "Your password"
                : `At least ${PASSWORD_MIN_LENGTH} characters`
            }
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError("");
              setConfirmError("");
            }}
            aria-invalid={!!passwordError}
            ref={passwordRef}
            autoComplete={alreadyCreated ? "current-password" : "new-password"}
            disabled={busy || !details}
          />
          {passwordError ? (
            <Text size="xs" weight="medium" variant="destructive">
              {passwordError}
            </Text>
          ) : null}
        </Box>
      </Box>

      {alreadyCreated ? null : (
        <Box gap="base">
          <Text
            as="label"
            htmlFor="invite-confirm"
            size="sm"
            weight="medium"
            variant="muted"
          >
            Confirm Password
          </Text>
          <Box gap="sm">
            <PasswordInput
              id="invite-confirm"
              size="xl"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmError("");
              }}
              aria-invalid={!!confirmError}
              autoComplete="new-password"
              disabled={busy || !details}
            />
            {confirmError ? (
              <Text size="xs" weight="medium" variant="destructive">
                {confirmError}
              </Text>
            ) : null}
          </Box>
        </Box>
      )}

      {formError ? (
        <Alert variant="error">
          <Icon name="alert-circle" />
          <AlertTitle>{formError}</AlertTitle>
        </Alert>
      ) : null}

      <Box direction="column" paddingTop="lg">
        {/* Enabled while fields are empty — the handler owns the checks and
            reads them inline. Only an invite still loading disables it. */}
        <Button
          type="submit"
          size="xl"
          loading={submitting || loading}
          disabled={!details || googlePending}
        >
          {alreadyCreated ? "Log In & Accept Invitation" : "Accept Invitation"}
        </Button>
      </Box>
    </Box>
  );
}
